import PropTypes from "prop-types";
import {get, startCase} from "lodash";
import React from "react";
import {useTranslation} from "@unibuddy/intl";
import {Box} from "@unibuddy/patron";
import Flag from "shared/Buddies/components/Flag/Flag";
import ChatNowButton from "shared/Blog/components/ChatNowButton/ChatNowButton";
import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import MentorCardBanner from "shared/Mentor/components/MentorCardBanner/MentorCardBanner";
import MentorCardDegree from "shared/Mentor/components/MentorCardDegree/MentorCardDegree";
import MentorCardName from "shared/Mentor/components/MentorCardName/MentorCardName";
import MentorCardSectionItem from "shared/Mentor/components/MentorCardSectionItem/MentorCardSectionItem";
import MentorCardSeparator from "shared/Mentor/components/MentorCardSeparator/MentorCardSeparator";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";

import s from "./MentorPreviewCard.pcss";

const TopRightComp = ({isMatched, degreeLevel}) => {
    if (isMatched && degreeLevel === "Alumni") {
        return (
            <div data-test-id="both">
                <i className="fa fa-star fa-lg" aria-hidden="true" />
                &nbsp; Alumni
            </div>
        );
    } else if (isMatched) {
        return <i data-test-id="star" className="fa fa-star fa-lg" aria-hidden="true" />;
    } else if (degreeLevel === "Alumni") {
        return "Alumni";
    }
    return null;
};

const MentorPreviewCard = (props) => {
    const {t} = useTranslation("mentorCard");
    const isShowCountryFlagEnabled = isFeatureEnabled("showCountryFlag", props.university);
    const isDegreeTypesEnabled = isFeatureEnabled("enableDegreeTypes", props.university);
    const degreeTypeOrder = get(props.university, "degreeTypeOrder", []);
    const degrees = {};
    props.degrees.forEach((obj) => {
        if (obj.degree && obj.degreeType) {
            if (degrees[obj.degreeType]) degrees[obj.degreeType].push(obj.degree.name);
            else degrees[obj.degreeType] = [obj.degree.name];
        }
    });
    const orderedDegrees = [];
    degreeTypeOrder.forEach((degree) => {
        if (degrees[degree.type])
            orderedDegrees.push({
                degreeType: degree.type,
                degree: degrees[degree.type],
            });
    });

    const totalDegreeRows = props.degrees.length + orderedDegrees.length;
    const isNotAlumni = props.degreeLevel !== "Alumni";
    const shouldShowPreviousQualification = isDegreeTypesEnabled
        ? isNotAlumni && totalDegreeRows <= 5
        : isNotAlumni;
    const shouldShowAboutMe = isDegreeTypesEnabled ? totalDegreeRows <= 9 : true;
    const bio = () =>
        props.bio.length > 200 ? `${props.bio.substring(0, 200)}...` : props.bio.substring(0, 200);
    return (
        <Box
            maxW="400px"
            minH="455px"
            borderColor="#e4e8eb"
            borderWidth="1px"
            borderRadius={"xsmall"}
            backgroundColor="white"
        >
            <MentorCardBanner
                name={props.firstName}
                className={s.banner}
                lastSeen={props.lastSeen}
                profilePhoto={props.profilePhoto}
                imageLink={`${props.rootUrl}/buddies/students/${props.mentorId}?buddyPosition=${props.buddyPosition}`}
                onImageClicked={() => props.onAvatarClicked(props.mentorId)}
                topRight={
                    <TopRightComp isMatched={props.isMatched} degreeLevel={props.degreeLevel} />
                }
            />

            <MentorCardName name={props.firstName} />

            {!isDegreeTypesEnabled ? <MentorCardDegree degree={props.degreeName} /> : null}

            {props.universityName ? (
                <p className={s.university} data-test-id="university">
                    {props.universityName}
                </p>
            ) : null}

            <div className={s.chatNow}>
                <ChatNowButton
                    onChatNow={props.onChatNow}
                    name={props.firstName}
                    className={s.chatNowButton}
                />
            </div>

            <MentorCardSeparator />
            {isDegreeTypesEnabled ? (
                <React.Fragment>
                    {orderedDegrees.map((degree) => {
                        return (
                            <MentorCardSectionItem
                                label={t(
                                    degree.degreeType.toLowerCase(),
                                    `${startCase(degree.degreeType)}(S)`,
                                )}
                                id="degreesSection"
                            >
                                {degree.degree.map((degreeName) => (
                                    <div>{degreeName}</div>
                                ))}
                            </MentorCardSectionItem>
                        );
                    })}
                    <MentorCardSeparator />
                </React.Fragment>
            ) : null}

            <MentorCardSectionItem label={t("iComeFrom", "I come From")}>
                {props.placeLabel}
                &nbsp;
                {isShowCountryFlagEnabled && (
                    <Flag
                        countryCode={props.countryCode}
                        primaryFlag={props.primaryFlag}
                        secondaryFlag={props.secondaryFlag}
                    />
                )}
            </MentorCardSectionItem>

            {shouldShowPreviousQualification ? (
                <MentorCardSectionItem
                    label={t("previousStudies", "Previous studies")}
                    id="pastQualificationSection"
                    data-test-id="pastQualificationSection"
                >
                    {props.pastQualification}
                </MentorCardSectionItem>
            ) : null}

            {shouldShowAboutMe ? (
                <MentorCardSectionItem label={t("aboutMe", "About me")} id="aboutMeSection">
                    {totalDegreeRows === 2 ? (
                        bio()
                    ) : (
                        <React.Fragment>
                            {props.shortBio}
                            ...
                        </React.Fragment>
                    )}
                </MentorCardSectionItem>
            ) : null}

            <div className={s.seeMore}>
                <ThemedLink
                    id={`read-more-${props.mentorId}`}
                    className={s.seeMoreLink}
                    onClick={() => props.onSeeMoreClicked(props.mentorId)}
                    to={`${props.rootUrl}/buddies/students/${props.mentorId}?buddyPosition=${props.buddyPosition}`}
                >
                    {t("readMore", `Read more about ${props.firstName} ...`, {
                        name: props.firstName,
                    })}
                </ThemedLink>
            </div>
        </Box>
    );
};
MentorPreviewCard.propTypes = {
    mentorId: PropTypes.string.isRequired,
    profilePhoto: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    degreeLevel: PropTypes.string.isRequired,
    pastQualification: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    placeLabel: PropTypes.string.isRequired,
    shortBio: PropTypes.string.isRequired,
    rootUrl: PropTypes.string.isRequired,
    onChatNow: PropTypes.func.isRequired,
    onAvatarClicked: PropTypes.func,
    onSeeMoreClicked: PropTypes.func,
    isMatched: PropTypes.bool,
    lastSeen: PropTypes.string,
    degreeName: PropTypes.string,
    degrees: PropTypes.array,
};

MentorPreviewCard.defaultProps = {
    lastSeen: null,
    isMatched: false,
    degreeName: "",
    degrees: [],
    onAvatarClicked: () => true,
    onSeeMoreClicked: () => true,
};

export default MentorPreviewCard;
